import { Row, Space, Switch, Table } from 'antd';
import { TableRowSelection } from 'antd/lib/table/interface';
import { ItemMenuTreeTableNode } from './convertItemMenuItemsToTreeTableData';
import { useTreeTableData } from './useTreeTableData';
import { useMenuItemColumns } from './useMenuItemColumns';
import styles from './ItemMenuTableTree.module.scss';
import classnames from 'classnames';
import { memo, useEffect, useMemo, useState } from 'react';
import { dataNodeUtils } from '@/components';
import { useBool } from '@/utils';
import { useTranslation } from 'react-i18next';
import { useVT } from 'virtualizedtableforantd4';

const rowSelection: TableRowSelection<ItemMenuTreeTableNode> = {
  type: 'checkbox',
  checkStrictly: false,
  getCheckboxProps: (record: ItemMenuTreeTableNode) => ({
    disabled: !!record.itemDescription,
  }),
};

export function ItemMenuTableTreeView(props: {
  id: string;
  communityId: string;
}) {
  const { data, itemMenuCategories } = useTreeTableData(props);

  const expandedRowKeys = useMemo(
    () => itemMenuCategories.map((x) => x.categoryId),
    [itemMenuCategories],
  );

  return (
    <ItemMenuTableTree
      mode="view"
      dataSource={data}
      expandedRowKeys={expandedRowKeys}
    />
  );
}

interface Props {
  mode: 'edit' | 'view';
  onChange?: (selected: string[]) => void;
  expandedRowKeys: string[];
  dataSource: ItemMenuTreeTableNode[];
  selectedRowKeys?: string[];
  selectedCategoriesKeys?: string[];
  setSelectedCategories?: (keys: string[]) => void;
  setSelectedRowKeys?: (keys: string[]) => void;
}

function _ItemMenuTableTree(props: Props) {
  const {
    mode,
    onChange,
    expandedRowKeys,
    dataSource,
    selectedRowKeys,
    selectedCategoriesKeys,
  } = props;
  const isEditing = mode === 'edit';
  const [expandedKeys, setExpandedKeys] = useState<string[]>();

  const onExpand = (expanded: boolean, record: ItemMenuTreeTableNode) => {
    if (expanded) {
      setExpandedKeys((prev) => [...(prev ?? []), record.key]);
    } else {
      const keys = expandedKeys?.filter((x) => x !== record.key);
      setExpandedKeys(keys);
    }
  };

  const { t } = useTranslation(undefined, { keyPrefix: 'itemMenus.details' });

  const [hideSubcategories, , toggleHideSubCategories] = useBool(false);
  const [hideSubcategories2, setHideSubCategories2, toggleHideSubCategories2] =
    useBool(false);
  const [hideItems, , toggleHideItems] = useBool(false);

  useEffect(() => {
    if (hideSubcategories) setHideSubCategories2(true);
  }, [hideSubcategories, setHideSubCategories2]);

  const filteredDataSource = useMemo(() => {
    if (!hideSubcategories && !hideSubcategories2 && !hideItems)
      return dataSource;
    return dataNodeUtils.filterWithConditionWithProps(dataSource, (node) => {
      const itemNode = node as ItemMenuTreeTableNode;

      if (hideSubcategories && itemNode.subCategory1) return false;
      if (hideSubcategories2 && itemNode.subCategory2) return false;
      if (hideItems && (!!itemNode.item || itemNode.itemDescription))
        return false;

      return true;
    }) as ItemMenuTreeTableNode[];
  }, [dataSource, hideItems, hideSubcategories, hideSubcategories2]);

  useEffect(() => {
    setExpandedKeys(expandedRowKeys);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandedRowKeys]);

  const columns = useMenuItemColumns(
    isEditing,
    'categories',
    hideSubcategories,
    hideSubcategories2,
    hideItems,
    selectedCategoriesKeys ?? [],
  );
  const [vt] = useVT(() => ({ scroll: { y: 650 } }), []);

  return (
    <>
      <Row className="mb-2">
        <Space direction="horizontal" size={50}>
          <Space>
            <Switch
              checked={hideSubcategories}
              onChange={toggleHideSubCategories}
            />
            {t('hideSubCategories')}
          </Space>
          <Space>
            <Switch
              checked={hideSubcategories2}
              onChange={toggleHideSubCategories2}
            />
            {t('hideSubCategories2')}
          </Space>
          <Space>
            <Switch checked={hideItems} onChange={toggleHideItems} />
            {t('hideItems')}
          </Space>
        </Space>
      </Row>
      <Table<ItemMenuTreeTableNode>
        rowSelection={
          isEditing
            ? {
                ...rowSelection,
                onChange: onChange
                  ? (selected) => onChange(selected.map((x) => x.toString()))
                  : undefined,
                selectedRowKeys: selectedRowKeys,
              }
            : undefined
        }
        expandable={{
          expandIconColumnIndex: isEditing ? 5 : 3,
          expandIcon: ({ expanded, onExpand, record }) => (
            <span
              onClick={(e) => onExpand(record, e)}
              className={classnames(styles.expand, expanded && styles.expanded)}
            >
              {record.item ? record.item : null}
            </span>
          ),
          onExpand: onExpand,
          expandedRowKeys: expandedKeys,
        }}
        rowClassName={(record) => (record.hidden ? styles.hiddenRow : '')}
        indentSize={0}
        columns={columns}
        dataSource={filteredDataSource}
        pagination={false}
        scroll={{ y: 650 }}
        components={vt}
      />
    </>
  );
}

export const ItemMenuTableTree = memo(_ItemMenuTableTree);
